import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, RotateCcw, Trash2, Warehouse } from 'lucide-react'
import {
  Button, Card, CardBody, CardHeader, ErrorState, LoadingState, NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, addStockPlace, deleteStockPlace, fetchStockPlaces, formatCount, may,
  saveStockPlace, setStockPlaceActive, translateError,
  type StockLocationKind, type StockPlaceRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/**
 * **THE CODE IS SUGGESTED AND SHOWN, NEVER APPLIED SILENTLY.** It is typed once and never
 * again — every movement in the ledger will carry it — so the person adding a place sees
 * what they are committing to before they commit to it. The same rule Sprint 56 and
 * Sprint 63 follow, and the same derivation.
 */
const suggestCode = (name: string) =>
  name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24)

/* ============================================================================
   W-109 — THE PLACES A HOTEL KEEPS ITS LINEN.  Sprint 69.

   ── THE GAP W-104 EXPOSED ──────────────────────────────────────────────────────
   Every screen in this area was built on top of a set of places **only the demonstration
   seed could create**. `SeedData` makes a store and a laundry; `DemoData` makes the four
   floor pantries. A real hotel handed this product could not say *"we have a pantry on the
   fourth floor"* at all — and nothing said so, because the demo always had them.

   Sprint 67 made that impossible to ignore: a par belongs to a place, and a hotel that
   cannot create a place cannot set a par for it.

   ── RETIRED, NOT REMOVED, ONCE THE LEDGER HAS TOUCHED IT ───────────────────────
   The ledger points at these rows. Removing one the ledger mentions would leave the
   history of the hotel's linen pointing at nothing, and the ledger is the single thing in
   this product that cannot be recovered from being wrong. The row carries its own
   movement count so the button is only drawn where it would work.

   ── AND A TROLLEY IS NOT MADE HERE ─────────────────────────────────────────────
   It belongs to a pair and is created with the team that holds it (M-09). It is LISTED, so
   a store keeper can see it and set a par on it, and it is not addable — two ways to create
   the same thing is how two of them end up existing for one pair.
   ========================================================================= */

export function StockPlaces({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()

  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [nameAr, setNameAr] = useState('')
  const [kind, setKind] = useState<StockLocationKind>('PANTRY')
  const [refusal, setRefusal] = useState<string>()

  const places = useQuery({
    queryKey: ['stock-places', propertyId],
    queryFn: ({ signal }) => fetchStockPlaces(propertyId, { signal }),
    retry: false,
  })

  const settled = (rows: StockPlaceRow[]) => {
    queryClient.setQueryData(['stock-places', propertyId], rows)
    /* The replenishment board and the stock board are both lists OF these places. */
    void queryClient.invalidateQueries({ queryKey: ['replenishment', propertyId] })
    void queryClient.invalidateQueries({ queryKey: ['stock-levels', propertyId] })
    setRefusal(undefined)
  }

  const refused = (e: unknown) => setRefusal(String(translateError(e, locale).message))

  const add = useMutation({
    mutationFn: () => addStockPlace(propertyId, {
      code: suggestCode(name), name: name.trim(), nameAr: nameAr.trim() || null, kind,
    }),
    onSuccess: (rows) => { settled(rows); setAdding(false); setName(''); setNameAr('') },
    onError: refused,
  })

  const rename = useMutation({
    mutationFn: (input: { id: string; name: string; nameAr: string | null }) =>
      saveStockPlace(propertyId, input.id, { name: input.name, nameAr: input.nameAr }),
    onSuccess: settled,
    onError: refused,
  })

  const setActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      setStockPlaceActive(propertyId, input.id, input.isActive),
    onSuccess: settled,
    onError: refused,
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteStockPlace(propertyId, id),
    onSuccess: settled,
    onError: refused,
  })

  if (!may('admin.stockPars')) return <NoPermissionState />
  if (places.isLoading && !places.error) return <LoadingState rows={5} />

  if (places.error && !places.data) {
    if (places.error instanceof ApiError && places.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(places.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={places.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const rows = places.data!

  return (
    <div className="space-y-4 p-4">
      {refusal && (
        <p role="alert" className="text-sm" style={{ color: 'var(--hk-hk-dirty-text)' }}>
          {refusal}
        </p>
      )}

      <Card>
        <CardHeader
          title={t.plTitle}
          icon={<Warehouse className="size-4" />}
          actions={
            <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="size-3.5" aria-hidden />
              {t.plAdd}
            </Button>
          }
        />

        {adding && (
          <CardBody className="space-y-2 border-b border-border">
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 text-[12px]">
                {t.plName}
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-label={t.plName}
                  className="w-56 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
                />
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                {t.plNameAr}
                <input
                  value={nameAr}
                  onChange={(e) => setNameAr(e.target.value)}
                  dir="rtl"
                  aria-label={t.plNameAr}
                  className="w-56 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
                />
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                {t.plKind}
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as StockLocationKind)}
                  aria-label={t.plKind}
                  className="rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
                >
                  {/* **NO TROLLEY.** It belongs to a pair and is made with the team. */}
                  <option value="PANTRY">{t.plKinds.PANTRY}</option>
                  <option value="STORE">{t.plKinds.STORE}</option>
                  <option value="LAUNDRY">{t.plKinds.LAUNDRY}</option>
                </select>
              </label>

              <Button
                type="button"
                size="sm"
                disabled={name.trim() === '' || add.isPending}
                onClick={() => add.mutate()}
              >
                {t.save}
              </Button>
              <Button type="button" size="sm" variant="ghost"
                      onClick={() => { setAdding(false); setName(''); setNameAr('') }}>
                {t.cancel}
              </Button>
            </div>

            {/* **SHOWN, NOT APPLIED SILENTLY.** The code is typed once and never again —
                every movement in the ledger carries it. */}
            {name.trim() !== '' && (
              <p className="text-[12px] text-muted-foreground">
                {fill(t.plCodeWillBe, { code: suggestCode(name) })}
              </p>
            )}
          </CardBody>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                <th className="px-3 py-2 text-start font-medium">{t.plName}</th>
                <th className="px-3 py-2 text-start font-medium">{t.plKind}</th>
                <th className="px-3 py-2 text-end font-medium">{t.plHolding}</th>
                <th className="px-3 py-2 text-end font-medium">{t.plPars}</th>
                <th className="px-3 py-2 text-end font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <PlaceRow
                  key={row.stockLocationId}
                  row={row}
                  busy={rename.isPending || setActive.isPending || remove.isPending}
                  onRename={(next, nextAr) =>
                    rename.mutate({ id: row.stockLocationId, name: next, nameAr: nextAr })}
                  onSetActive={(isActive) =>
                    setActive.mutate({ id: row.stockLocationId, isActive })}
                  onRemove={() => remove.mutate(row.stockLocationId)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-[12px] text-muted-foreground">{t.plNote}</p>
    </div>
  )
}

function PlaceRow({
  row, busy, onRename, onSetActive, onRemove,
}: {
  row: StockPlaceRow
  busy: boolean
  onRename: (name: string, nameAr: string | null) => void
  onSetActive: (isActive: boolean) => void
  onRemove: () => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [name, setName] = useState(row.name)
  const [nameAr, setNameAr] = useState(row.nameAr ?? '')

  const dirty = name.trim() !== row.name || nameAr.trim() !== (row.nameAr ?? '')

  return (
    <tr className={`border-b border-border/60 last:border-0 ${
      row.isActive ? '' : 'opacity-60'}`}
    >
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={`${row.code} — ${t.plName}`}
            className="w-44 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
          />
          <input
            value={nameAr}
            onChange={(e) => setNameAr(e.target.value)}
            dir="rtl"
            aria-label={`${row.code} — ${t.plNameAr}`}
            className="w-44 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
          />
          {dirty && (
            <Button type="button" size="sm" disabled={busy || name.trim() === ''}
                    onClick={() => onRename(name.trim(), nameAr.trim() || null)}>
              {t.save}
            </Button>
          )}
        </div>
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          <span dir="ltr">{row.code}</span>
          {row.floorCode && <>{' · '}{fill(t.plOnFloor, { floor: row.floorCode })}</>}
          {/* A trolley's owner, read-only: the pair is the arrangement, not this screen's. */}
          {row.teamName && <>{' · '}<bdi>{row.teamName}</bdi></>}
          {!row.isActive && <>{' · '}{t.plRetired}</>}
        </span>
      </td>

      <td className="px-3 py-2 text-muted-foreground">{t.plKinds[row.kind]}</td>

      <td className="px-3 py-2 text-end tnum text-muted-foreground">
        {formatCount(row.itemsHeld, locale)}
      </td>
      <td className="px-3 py-2 text-end tnum text-muted-foreground">
        {formatCount(row.parsSet, locale)}
      </td>

      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1.5">
          <Button type="button" size="sm" variant="ghost" disabled={busy}
                  onClick={() => onSetActive(!row.isActive)}>
            {row.isActive
              ? t.plRetire
              : <><RotateCcw className="size-3.5" aria-hidden />{t.plRestore}</>}
          </Button>

          {/* **DRAWN ONLY WHERE IT WOULD WORK.** A button that exists to be refused
              teaches people to distrust the screen — #144's lesson, applied to an
              administration list. The row carries its own movement count for exactly this. */}
          {row.movementsEver === 0 ? (
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onRemove}>
              <Trash2 className="size-3.5" aria-hidden />
              {t.plRemove}
            </Button>
          ) : (
            <span className="text-[11.5px] text-muted-foreground">
              {fill(t.plInLedger, { count: formatCount(row.movementsEver, locale) })}
            </span>
          )}
        </div>
      </td>
    </tr>
  )
}
